import type { SamModule } from '@/components/SAM/samActionTypes';

// ─── shared type ──────────────────────────────────────────────────────────────

export type Rec = Record<string, unknown>;

// ─── pill color maps ──────────────────────────────────────────────────────────

export const CPRIORITY: Record<string, string> = {
  high:   'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low:    'bg-gray-100 text-gray-700',
};
export const CSTATUS: Record<string, string> = {
  done:        'bg-green-100 text-green-800',
  pending:     'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
};

// ─── CPill component ──────────────────────────────────────────────────────────

export function CPill({ label }: { label: string }) {
  const k = label.toLowerCase().replace(/[\s-]+/g, '_');
  const cls = CPRIORITY[k] ?? CSTATUS[k] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`${cls} text-[11px] px-2.5 py-0.5 rounded-lg font-medium whitespace-nowrap`}>
      {label}
    </span>
  );
}

// ─── label / date helpers ─────────────────────────────────────────────────────

export function humanizeLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

export const sv = (v: unknown) => (v == null ? '' : String(v));

// ─── module metadata ──────────────────────────────────────────────────────────

export const MODULE_ID_FIELD: Record<SamModule, string> = {
  tasks: 'task_id', notes: 'note_id', properties: 'property_id', meetings: 'meeting_id',
  transactions: 'transaction_id', proposals: 'proposal_id', jobs: 'job_id',
  calendar: 'event_id', billing: 'invoice_id', notifications: 'notification_id',
  users: 'user_id', roles: 'tenant_role_id', communications: 'communication_id',
  my_todos: 'item_id',
  // 'workflow' is a single-object surface (WorkflowPanel/WorkflowCard), never rendered through the
  // generic InlineCards list — this entry only exists to satisfy the exhaustive Record.
  workflow: 'engagement_id', engagements: 'engagement_id',
  // PCR-FEAT-02 / 03 slices — cards exist on the backend, list panels wire up here as
  // dedicated components land. Entries kept exhaustive so the Record<SamModule, string>
  // typecheck holds regardless of which surfaces have shipped visually.
  contacts: 'contact_id', warranties: 'warranty_id',
  measurements: 'measurement_id', media: 'media_id',
};
export const MODULE_NOUN: Record<SamModule, string> = {
  tasks: 'task', notes: 'note', properties: 'property', meetings: 'meeting',
  transactions: 'transaction', proposals: 'proposal', jobs: 'job',
  calendar: 'event', billing: 'invoice', notifications: 'notification',
  users: 'user', roles: 'role', communications: 'communication',
  my_todos: 'item',
  workflow: 'workflow', engagements: 'engagement',
  contacts: 'contact', warranties: 'warranty',
  measurements: 'measurement', media: 'media',
};

// ─── card identity + display info ─────────────────────────────────────────────

export function cardId(module: SamModule, r: Rec) {
  return sv(r[MODULE_ID_FIELD[module]] ?? r._id ?? '');
}

export function cardInfo(module: SamModule, r: Rec) {
  switch (module) {
    case 'tasks':
      return { title: sv(r.title) || 'Task', sub: r.due_date ? `Due ${fmtDate(sv(r.due_date))}` : '', priority: sv(r.priority), status: sv(r.status) };
    case 'notes': {
      const tags = Array.isArray(r.custom_tags) ? (r.custom_tags as string[]).join(', ') : '';
      const summary = sv(r.summary || r.review_summary);
      return {
        title: sv(r.title || r.agent_title) || (summary ? summary.slice(0, 48) : 'Note'),
        sub: [humanizeLabel(sv(r.note_type)), tags].filter(Boolean).join(' · '),
        priority: '',
        status: sv(r.status),
      };
    }
    case 'properties': {
      const addr = (r.address as Rec) || {};
      return { title: sv(addr.normalized_address || addr.street || r.title || r.name) || 'Property', sub: [sv(addr.city || r.city), sv(r.owner_name)].filter(Boolean).join(' · '), priority: '', status: sv(r.status) };
    }
    case 'meetings':
      return { title: sv(r.title || r.subject) || 'Meeting', sub: r.scheduled_at ? fmtDate(sv(r.scheduled_at)) : '', priority: '', status: sv(r.status) };
    case 'transactions':
      return { title: sv(r.description || r.type || r.title) || 'Transaction', sub: r.amount ? `$${sv(r.amount)}` : (r.date ? fmtDate(sv(r.date)) : ''), priority: '', status: sv(r.status) };
    case 'proposals':
      return { title: sv(r.title || r.project_name || r.name) || 'Proposal', sub: r.value ? `$${sv(r.value)}` : sv(r.client_name) || '', priority: '', status: sv(r.status) };
    case 'jobs':
      return { title: sv(r.title || r.job_name || r.name) || 'Job', sub: sv(r.client_name || r.location || r.address) || '', priority: sv(r.priority), status: sv(r.status) };
    case 'calendar':
      return { title: sv(r.title || r.event_name || r.name) || 'Event', sub: (r.start_date || r.scheduled_at) ? fmtDate(sv(r.start_date || r.scheduled_at)) : '', priority: '', status: sv(r.status) };
    case 'billing':
      return { title: sv(r.invoice_number || r.title || r.description) || 'Invoice', sub: r.amount ? `$${sv(r.amount)}` : (r.due_date ? `Due ${fmtDate(sv(r.due_date))}` : ''), priority: '', status: sv(r.status) };
    case 'notifications':
      return { title: sv(r.message || r.subject || r.title) || 'Notification', sub: r.created_at ? fmtDate(sv(r.created_at)) : '', priority: sv(r.priority), status: sv(r.read ? 'read' : (r.status || 'unread')) };
    case 'users': {
      const roleNames = Array.isArray(r.role_names) ? (r.role_names as string[]).join(', ') : '';
      return { title: sv(r.name || r.email) || 'User', sub: [sv(r.email), roleNames, sv(r.tenant_name)].filter(Boolean).join(' · '), priority: '', status: sv(r.status) };
    }
    case 'roles': {
      const mappings = Array.isArray(r.system_role_mappings) ? (r.system_role_mappings as string[]).join(', ') : '';
      return { title: sv(r.name) || 'Role', sub: [mappings, sv(r.tenant_name)].filter(Boolean).join(' · '), priority: r.is_default ? 'default' : '', status: r.visibility === false ? 'hidden' : 'visible' };
    }
    case 'my_todos': {
      const sub = [sv(r.context_label), r.due_on ? `Due ${fmtDate(sv(r.due_on))}` : ''].filter(Boolean).join(' · ');
      return { title: sv(r.title) || 'Item', sub, priority: sv(r.priority), status: sv(r.item_type) };
    }
    case 'communications': {
      const first = (Array.isArray(r.recipients) ? (r.recipients as Rec[])[0] : undefined) || {};
      const ctx = (r.context as Rec) || {};
      const channel = sv(r.communication_type);
      const title = sv(r.subject) || (first.name ? `To ${sv(first.name)}` : (channel === 'sms' ? 'SMS' : 'Email'));
      const sub = [humanizeLabel(channel) || 'Message', sv(ctx.display_label) || sv(first.name || first.email || first.phone)].filter(Boolean).join(' · ');
      return { title, sub, priority: '', status: sv(r.status) };
    }
    case 'engagements': {
      const labels = (r.display_labels as Rec) || {};
      return {
        title: sv(r.title) || 'Engagement',
        location: sv(r.property_name) || undefined,
        sub: [humanizeLabel(sv(r.engagement_type)), sv(labels.stage)].filter(Boolean).join(' · '),
        priority: '',
        status: sv(labels.status || r.current_status_code),
      };
    }
    case 'contacts': {
      return {
        title: sv(r.display_name || r.first_name || r.organization_name) || 'Contact',
        sub: [sv(r.primary_phone), sv(r.primary_email)].filter(Boolean).join(' · '),
        priority: '',
        status: sv(r.status || 'active'),
      };
    }
    case 'warranties': {
      return {
        title: sv(r.provider) || 'Warranty',
        sub: [humanizeLabel(sv(r.warranty_type)), r.expiry_date ? `Expires ${fmtDate(sv(r.expiry_date))}` : ''].filter(Boolean).join(' · '),
        priority: '',
        status: sv(r.derived_status || r.status || 'active'),
      };
    }
    case 'measurements': {
      const values = (r.values as Rec) || {};
      return {
        title: `${sv(r.how_obtained) || 'Measurement'}${r.provider ? ` (${sv(r.provider)})` : ''}`,
        sub: [values.squares ? `${values.squares} sq` : '', values.pitch ? `${sv(values.pitch)} pitch` : ''].filter(Boolean).join(' · '),
        priority: r.is_current_reference ? 'current' : '',
        status: sv(r.how_obtained),
      };
    }
    case 'media': {
      const tags = Array.isArray(r.classifications) ? (r.classifications as string[]).join(', ') : '';
      return {
        title: sv(r.caption || r.filename) || (sv(r.media_type) === 'video' ? 'Video' : 'Photo'),
        sub: tags,
        priority: '',
        status: sv(r.media_type),
      };
    }
    default:
      return { title: sv((r as Rec).title || (r as Rec).name) || module, sub: '', priority: '', status: '' };
  }
}

// ─── field / status metadata ──────────────────────────────────────────────────

export const HIDDEN = new Set(['_id', 'tenant_id', 'deleted_at', 'created_at', 'updated_at', '_related', 'user_id', 'joined_at', 'source']);
export const DATE_FIELDS = new Set(['due_date', 'scheduled_at', 'date', 'start_date', 'end_date']);
export const STATUS_OPTIONS: Record<string, string[]> = {
  tasks:         ['pending', 'in_progress', 'done'],
  notes:         ['draft', 'ready', 'saved', 'discarded', 'failed'],
  properties:    ['active', 'inactive', 'sold'],
  meetings:      ['scheduled', 'completed', 'cancelled'],
  transactions:  ['pending', 'completed', 'failed', 'refunded'],
  proposals:     ['draft', 'sent', 'accepted', 'rejected'],
  jobs:          ['pending', 'in_progress', 'completed', 'cancelled'],
  calendar:      ['scheduled', 'completed', 'cancelled'],
  billing:       ['draft', 'sent', 'paid', 'overdue', 'cancelled'],
  notifications: ['unread', 'read', 'archived'],
  users:         ['active', 'suspended', 'inactive'],
  roles:         [],
  communications: ['queued', 'sent', 'delivered', 'failed'],
  my_todos: [],
  // Engagement statuses are workflow-definition-driven (dynamic), not a fixed set — no options here.
  engagements: [],
};
